import { FormEvent, ReactNode } from 'react'
import { Page, PageHeader } from '@/components/page'
import { useForm } from '@inertiajs/react'
import { WizardGuide } from '@/components/layout/WizardGuide'
import { WizardLayout } from '../WizardLayout'
import { WizardActions } from '../WizardActions'
import { Button } from '@/components/ui/button'
import { formatCurrency } from '@/lib/utils'
import { useBasePath } from '@/lib/paths'
import { ArrowRight, Building2, Mail, ShieldCheck, UserRound } from 'lucide-react'
import { Tip } from '@/components/sketch/Tip'
import type { WizardFormationRef } from '@/types'

type BusinessAddress =
  | { provider: 'corppy' }
  | { provider: 'own'; street_address_1: string; street_address_2: string | null; city: string; state_province: string | null; postal_code: string | null; country_code: string }

type CountryOption = { id: string; code: string; name: string }

type Founder = {
  id: string
  principal_kind: 'individual' | 'entity'
  full_name: string
  legal_name: string
  email: string
  country_code: string
  date_of_birth: string | null
  street_address_1: string | null
  street_address_2: string | null
  city: string | null
  state_province: string | null
  postal_code: string | null
  roles: string[]
  controlling_person_name: string | null
}

type Owner = { full_name: string; ownership_percentage: number | null }

type Structure =
  | { kind: 'llc'; management_structure: string; managers: string[] }
  | {
      kind: 'corporation'
      authorized_common_shares: number | null
      par_value_policy: string | null
      par_value: string | null
      employee_pool_basis_points: number | null
      vesting_months: number | null
      cliff_months: number | null
      vesting_start_policy: string | null
      acceleration_policy: string | null
      consideration_type: string | null
      aggregate_purchase_price: string | null
      shareholders: { full_name: string; shares: number }[]
      directors: string[]
      president: string | null
      secretary: string | null
    }

type Tax = {
  responsible_party_name: string | null
  tax_id_status: 'ssn' | 'itin' | 'neither' | null
  ein_route: 'with_ssn' | 'without_ssn' | null
}

type CompanyFacts = {
  online_presence_kind: string | null
  online_presence_url: string | null
  primary_contact: { email: string; phone: string } | null
}

type Applicant = { principal_id: string; full_name: string } | null

type FilingAnswer = {
  identifier: string
  label: string
  value: string | null
  options: { label: string; value: string }[]
}

type Props = {
  formation: WizardFormationRef & { founder_residency: 'us_resident' | 'non_us_resident' | null }
  entity: {
    name: string
    suffix: string
    jurisdiction_code: string
    state_name: string | null
    service_fee_cents: number
    state_fee_cents: number
    total_fee_cents: number
    processing_days_min: number | null
    processing_days_max: number | null
  }
  founders: Founder[]
  owners: Owner[]
  structure: Structure
  company_description: string | null
  company: CompanyFacts
  applicant: Applicant
  filing_answers: FilingAnswer[] | null
  backup_names: string[]
  business_address: BusinessAddress | null
  founder_country: CountryOption | null
  tax: Tax
  ready: boolean
  review_blockers: string[]
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-label={title}>
      <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{title}</h2>
      <div className="rounded-lg border divide-y">{children}</div>
    </section>
  )
}

function Line({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="px-4 py-3">
      <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
      <div className="text-sm text-foreground">{children}</div>
    </div>
  )
}

function managementLabel(value: string) {
  return value === 'member_managed' ? 'Member-managed' : 'Manager-managed'
}

function considerationLabel(value: string) {
  if (value === 'ip') return 'Contributed intellectual property'
  if (value === 'nominal') return 'Nominal cash'
  if (value === 'cash') return 'Cash'
  return value
}

function accelerationLabel(value: string) {
  if (value === 'double_trigger') return 'Double-trigger'
  if (value === 'single_trigger') return 'Single-trigger'
  if (value === 'none') return 'None'
  return value
}

function taxStatusLabel(status: Tax['tax_id_status']) {
  if (status === 'ssn') return 'SSN'
  if (status === 'itin') return 'ITIN'
  if (status === 'neither') return 'No SSN or ITIN'
  return 'Not recorded'
}

function presenceLabel(kind: string | null) {
  if (kind === 'website') return 'Website'
  if (kind === 'social') return 'Social profile'
  if (kind === 'app') return 'App'
  if (kind === 'prelaunch') return 'Not online yet'
  return 'Not recorded'
}

function addressLine(address: BusinessAddress | null) {
  if (!address) return 'Not recorded'
  if (address.provider === 'corppy') return 'Provided by Corppy (included)'
  const parts = [
    address.street_address_1,
    address.street_address_2,
    address.city,
    [address.state_province, address.postal_code].filter(Boolean).join(' '),
    address.country_code,
  ].filter(Boolean)
  return parts.join(', ')
}

function formatDate(value: string) {
  return new Date(`${value}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function residentialAddress(person: Founder) {
  const parts = [
    person.street_address_1,
    person.street_address_2,
    person.city,
    [person.state_province, person.postal_code].filter(Boolean).join(' '),
    person.country_code,
  ].filter(Boolean)
  return parts.join(', ') || null
}

function processingTime(entity: Props['entity']) {
  if (entity.processing_days_min == null || entity.processing_days_max == null) return 'Not recorded'
  if (entity.processing_days_min === entity.processing_days_max) return `${entity.processing_days_min} business days`
  return `${entity.processing_days_min}–${entity.processing_days_max} business days`
}

export default function ReviewsEdit({
  formation, entity, founders, owners, structure, company_description, company, applicant,
  filing_answers, backup_names, business_address, founder_country, tax, ready, review_blockers,
}: Props) {
  const basePath = useBasePath()
  const form = useForm<Record<string, never>>({})

  const residence = founder_country ? founder_country.name : 'Outside the United States'

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    form.patch(`${basePath}/formations/${formation.id}/wizard/review`)
  }

  return (
    <WizardLayout
      title="Review"
      currentStep="review"
      formationId={formation.id}
      resumeStep={formation.current_wizard_step}
      breadcrumbs={[{ label: 'Businesses', href: 'businesses' }, { label: 'Review' }]}
      guide={
        <WizardGuide title="Before you continue">
          <p>
            Check that everything is right. Corppy uses these facts to generate your filing documents — nothing is
            filed or charged yet.
          </p>
        </WizardGuide>
      }
      tips={
        <Tip seed="review-read-only">
          This is a summary, not a form. Continue on to your documents; payment comes after you sign.
        </Tip>
      }
    >
      <Page>
        <PageHeader
          title={<>Everything, in one place</>}
          subtitle={<>Check it over — this is what Corppy will use to prepare your filing.</>}
        />

        {!ready && review_blockers.length > 0 && (
          <p className="text-sm text-destructive" role="alert">
            Your application isn't complete yet — finish the earlier steps to continue.
          </p>
        )}

        <Section title="Where you're based">
          <Line label="Residence">{residence}</Line>
          <Line label="Formation state">{entity.state_name ?? entity.jurisdiction_code}</Line>
        </Section>

        <Section title="Entity and name">
          <Line label="Entity type">{entity.name} · {entity.suffix}</Line>
          <Line label="Company name">
            <span className="font-medium">{formation.legal_name}</span>
            {backup_names.length > 0 && (
              <span className="block text-xs text-muted-foreground mt-0.5">
                Backups: {backup_names.join(', ')}
              </span>
            )}
          </Line>
        </Section>

        <Section title="Company">
          <Line label="What you do">{company_description || 'Not recorded'}</Line>
          <Line label="Online presence">
            {presenceLabel(company?.online_presence_kind ?? null)}
            {company?.online_presence_url && (
              <span className="block text-xs text-muted-foreground">{company.online_presence_url}</span>
            )}
          </Line>
          <Line label="Primary contact">
            {company?.primary_contact
              ? `${company.primary_contact.email} · ${company.primary_contact.phone}`
              : 'Not recorded'}
          </Line>
          <Line label="Business address">{addressLine(business_address)}</Line>
        </Section>

        <Section title="People">
          <Line label="Running the filing">{applicant?.full_name ?? 'Not recorded'}</Line>
          {founders.length > 0 ? (
            founders.map((person) => (
              <div key={person.id} className="px-4 py-3">
                <p className="text-sm font-medium text-foreground flex items-center gap-2">
                  {person.principal_kind === 'entity' ? (
                    <Building2 className="size-3.5 text-muted-foreground" aria-hidden="true" />
                  ) : (
                    <UserRound className="size-3.5 text-muted-foreground" aria-hidden="true" />
                  )}
                  {person.full_name || person.legal_name}
                </p>
                {person.principal_kind === 'entity' && person.controlling_person_name && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Controlled by {person.controlling_person_name}
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-1">{person.email}</p>
                {person.principal_kind === 'individual' && (
                  <>
                    {person.date_of_birth && (
                      <p className="text-xs text-muted-foreground mt-0.5">Born {formatDate(person.date_of_birth)}</p>
                    )}
                    {residentialAddress(person) && (
                      <p className="text-xs text-muted-foreground mt-0.5">{residentialAddress(person)}</p>
                    )}
                  </>
                )}
                {person.roles.length > 0 && (
                  <p className="text-xs text-foreground mt-1">{person.roles.join(' · ')}</p>
                )}
              </div>
            ))
          ) : (
            <Line label="Founders">None recorded</Line>
          )}
        </Section>

        {filing_answers && filing_answers.length > 0 && (
          <Section title="Filing details">
            {filing_answers.map((answer) => (
              <Line key={answer.identifier} label={answer.label}>
                {answer.options.length > 0
                  ? answer.options.map((o) => o.label).join(', ') || answer.value || '—'
                  : answer.value || '—'}
              </Line>
            ))}
          </Section>
        )}

        <Section title={structure.kind === 'llc' ? 'Ownership and management' : 'Ownership and governance'}>
          {structure.kind === 'llc' ? (
            <>
              <Line label="Management">{managementLabel(structure.management_structure)}</Line>
              <Line label="Owners">
                {owners.map((owner, i) => (
                  <span key={owner.full_name + i} className="block">
                    {owner.full_name}{owner.ownership_percentage != null ? ` — ${owner.ownership_percentage}%` : ''}
                  </span>
                ))}
              </Line>
              {structure.managers.length > 0 && (
                <Line label="Managers">{structure.managers.join(', ')}</Line>
              )}
            </>
          ) : (
            <>
              <Line label="Authorized common shares">{(structure.authorized_common_shares ?? 0).toLocaleString()}</Line>
              <Line label="Par value">
                {structure.par_value_policy === 'par_value' ? `$${structure.par_value}` : 'No par value'}
              </Line>
              <Line label="Employee pool">
                {((structure.employee_pool_basis_points ?? 0) / 100).toFixed(0)}%
              </Line>
              <Line label="Founder shares">
                {structure.shareholders.map((s, i) => (
                  <span key={s.full_name + i} className="block">
                    {s.full_name} — {s.shares.toLocaleString()} shares
                  </span>
                ))}
              </Line>
              <Line label="Vesting">
                {structure.vesting_months} months with a {structure.cliff_months}-month cliff
              </Line>
              <Line label="Vesting starts">
                {structure.vesting_start_policy === 'incorporation_date'
                  ? 'On the incorporation date'
                  : 'On a specific date'}
              </Line>
              <Line label="Acceleration">{accelerationLabel(structure.acceleration_policy ?? '')}</Line>
              <Line label="Share consideration">{considerationLabel(structure.consideration_type ?? '')}</Line>
              <Line label="Aggregate purchase price">${structure.aggregate_purchase_price}</Line>
              <Line label="Directors">{structure.directors.join(', ')}</Line>
              {structure.president && <Line label="President">{structure.president}</Line>}
              {structure.secretary && <Line label="Secretary">{structure.secretary}</Line>}
            </>
          )}
        </Section>

        <Section title="EIN details">
          <Line label="Responsible party">{tax.responsible_party_name ?? 'Not recorded'}</Line>
          <Line label="Tax ID status">{taxStatusLabel(tax.tax_id_status)}</Line>
          <Line label="EIN">
            {tax.ein_route === 'with_ssn'
              ? 'Corppy will file the EIN using the standard electronic route.'
              : tax.ein_route === 'without_ssn'
                ? 'Corppy will file the EIN using the manual IRS route, which can take longer.'
                : 'Not recorded'}
          </Line>
        </Section>

        <Section title="Included with formation">
          <Line label="State filing">
            <span className="flex items-center gap-2">
              <ShieldCheck className="size-3.5 text-muted-foreground" aria-hidden="true" />
              {entity.jurisdiction_code} formation filing
            </span>
          </Line>
          <Line label="Registered agent">
            <span className="flex items-center gap-2">
              <ShieldCheck className="size-3.5 text-muted-foreground" aria-hidden="true" />
              Corppy registered agent — first year (included)
            </span>
          </Line>
          <Line label="EIN">
            <span className="flex items-center gap-2">
              <Mail className="size-3.5 text-muted-foreground" aria-hidden="true" />
              EIN application (included)
            </span>
          </Line>
          {business_address?.provider === 'corppy' && (
            <Line label="Business address">
              <span className="flex items-center gap-2">
                <Mail className="size-3.5 text-muted-foreground" aria-hidden="true" />
                Corppy business address (included)
              </span>
            </Line>
          )}
          <Line label="Estimated filing time">{processingTime(entity)}</Line>
        </Section>

        <div className="rounded-lg border border-foreground/15 bg-muted/40 p-5">
          <div className="flex items-baseline justify-between">
            <p className="text-sm font-medium text-foreground">Total due at checkout</p>
            <p className="text-xl font-semibold text-foreground">{formatCurrency(entity.total_fee_cents)}</p>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {formatCurrency(entity.service_fee_cents)} Corppy service fee + {formatCurrency(entity.state_fee_cents)}{' '}
            {entity.jurisdiction_code} filing fee. State filing, registered agent (first year), and the EIN application are
            included
            {business_address?.provider === 'corppy' ? ', along with the Corppy business address.' : '.'}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <WizardActions currentStep="review" formationId={formation.id} disabled={form.processing}>
            <Button type="submit" disabled={form.processing || !ready}>
              {form.processing ? 'Saving…' : 'Continue to documents'}
              <ArrowRight className="size-4" />
            </Button>
          </WizardActions>
        </form>
      </Page>
    </WizardLayout>
  )
}
